
import { Component, Input, Output, EventEmitter, Inject, OnChanges, SimpleChanges, HostListener, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminService, Customer } from '../../../core/services/admin.service';
import { AdminFeedbackService } from '../../../core/services/admin-feedback.service';
import { DialogService } from '../../../core/services/dialog.service';

@Component({
  selector: 'app-admin-customer-tab',
  standalone: false,
  template: require('./admin-customer-tab.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./admin-customer-tab.component.scss').default],
})
export class AdminCustomerTabComponent implements OnChanges {
  private readonly requiredCreateFields: Array<keyof Customer> = ['customername', 'legalname'];

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.btn-group')) {
      this.showMoreMenu = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.showMoreMenu = false;
  }

  @Input() customers: Customer[] = [];
  @Output() dataUpdated = new EventEmitter<void>();

  // Current selection
  currentIndex = 0;
  selectedCustomer: Customer | null = null;
  editCustomer: Partial<Customer> = {};
  initialEditCustomer: Partial<Customer> = {};
  originalId: number | null = null;

  // New record mode
  isNew = false;

  // Dirty flag
  isDirty = false;

  // Dropdown menu toggle
  showMoreMenu = false;
  private pendingSelectionId: number | null = null;
  private pendingSelectionIndex: number | null = null;

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(AdminService) private adminService: AdminService,
    @Inject(DialogService) private dialogService: DialogService,
    @Inject(AdminFeedbackService) private adminFeedback: AdminFeedbackService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customers']) {
      if (this.isNew || this.isDirty) {
        if (!this.isNew && this.selectedCustomer) {
          const selectedIndex = this.customers.findIndex((c) => c.customer_id === this.selectedCustomer?.customer_id);
          this.currentIndex = selectedIndex >= 0 ? selectedIndex : 0;
        } else if (this.customers.length === 0) {
          this.currentIndex = 0;
        } else {
          this.currentIndex = Math.min(this.currentIndex, this.customers.length - 1);
        }

        this.pendingSelectionId = null;
        this.pendingSelectionIndex = null;
        return;
      }

      if (this.customers.length > 0) {
        const preferredId = this.pendingSelectionId ?? this.originalId ?? this.selectedCustomer?.customer_id ?? null;
        const preferredIndex =
          preferredId === null ? -1 : this.customers.findIndex((c) => c.customer_id === preferredId);

        if (preferredIndex >= 0) {
          this.currentIndex = preferredIndex;
          this.selectCustomer(this.customers[preferredIndex]);
        } else if (this.pendingSelectionIndex !== null) {
          const clampedIndex = Math.min(this.pendingSelectionIndex, this.customers.length - 1);
          this.currentIndex = clampedIndex;
          this.selectCustomer(this.customers[clampedIndex]);
        } else {
          this.currentIndex = 0;
          this.selectCustomer(this.customers[0]);
        }
      } else {
        this.selectedCustomer = null;
        this.editCustomer = {};
        this.currentIndex = 0;
      }

      this.pendingSelectionId = null;
      this.pendingSelectionIndex = null;
    }
  }

  selectCustomer(cust: Customer): void {
    this.selectedCustomer = cust;
    this.originalId = cust.customer_id;
    this.editCustomer = { ...cust };
    this.initialEditCustomer = { ...cust };
    this.isNew = false;
    this.isDirty = false;
  }

  movePrev(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.selectCustomer(this.customers[this.currentIndex]);
    }
  }

  moveNext(): void {
    if (this.currentIndex < this.customers.length - 1) {
      this.currentIndex++;
      this.selectCustomer(this.customers[this.currentIndex]);
    }
  }

  onCustomerSelect(): void {
    if (this.selectedCustomer) {
      const index = this.customers.findIndex(c => c.customer_id === this.selectedCustomer?.customer_id);
      if (index >= 0) {
        this.currentIndex = index;
      }
      this.selectCustomer(this.selectedCustomer);
    }
  }

  addNew(): void {
    this.isNew = true;
    this.selectedCustomer = null;
    this.originalId = null;
    this.editCustomer = {
      customername: '',
      legalname: '',
      address1: '',
      address2: '',
      zipcode: '',
      city: '',
      country: 'Sverige',
      vatno: '',
    };
    this.initialEditCustomer = { ...this.editCustomer };
    this.isDirty = false;
  }

  save(): void {
    if (this.isNew) {
      this.adminService.createCustomer(this.editCustomer).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (result) => {
          this.pendingSelectionId = result?.insertId ?? null;
          this.isNew = false;
          this.isDirty = false;
          this.dataUpdated.emit();
        },
        error: (err) => this.adminFeedback.showCrudError('skapa', 'kund', err),
      });
    } else if (this.originalId !== null) {
      this.adminService.updateCustomer(this.originalId, this.editCustomer).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.pendingSelectionId = this.originalId;
          this.isDirty = false;
          this.dataUpdated.emit();
        },
        error: (err) => this.adminFeedback.showCrudError('uppdatera', 'kund', err),
      });
    }
  }

  cancel(): void {
    if (this.isNew) {
      this.isNew = false;
      if (this.customers.length > 0) {
        this.selectCustomer(this.customers[this.currentIndex]);
      } else {
        this.editCustomer = {};
        this.initialEditCustomer = {};
      }
    } else if (this.selectedCustomer) {
      this.selectCustomer(this.selectedCustomer);
      return;
    }
    this.isDirty = false;
  }

  async delete(): Promise<void> {
    if (this.originalId === null) {
      return;
    }

    const confirmed = await this.dialogService.confirm({
      title: 'Bekräfta borttagning',
      body: 'Är du säker på att du vill ta bort denna kund?',
      confirmText: 'Ta bort',
      cancelText: 'Avbryt',
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    this.pendingSelectionIndex = this.currentIndex;
    this.adminService.deleteCustomer(this.originalId!).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.dataUpdated.emit();
      },
      error: (err) => this.adminFeedback.showCrudError('ta bort', 'kund', err),
    });
  }

  duplicate(): void {
    if (!this.selectedCustomer) return;
    this.isNew = true;
    this.originalId = null;
    this.editCustomer = {
      ...this.selectedCustomer,
      customer_id: undefined,
      customername: this.selectedCustomer.customername + ' (kopia)',
    };
    this.initialEditCustomer = { ...this.selectedCustomer };
    this.isDirty = true;
  }

  onFieldChange(): void {
    this.isDirty = this.hasChanges();
  }

  isRequiredField(field: keyof Customer): boolean {
    return this.requiredCreateFields.includes(field);
  }

  isRequiredMissing(field: keyof Customer): boolean {
    if (!this.isNew || !this.isRequiredField(field)) {
      return false;
    }
    const value = this.editCustomer[field];
    return typeof value === 'string' ? value.trim() === '' : value == null;
  }

  hasMissingRequiredFields(): boolean {
    return this.requiredCreateFields.some((field) => this.isRequiredMissing(field));
  }

  isFieldDirty(field: keyof Customer): boolean {
    return this.normalizeFieldValue(this.editCustomer[field]) !== this.normalizeFieldValue(this.initialEditCustomer[field]);
  }

  hasChanges(): boolean {
    const fields = new Set<keyof Customer>([
      ...Object.keys(this.editCustomer),
      ...Object.keys(this.initialEditCustomer),
    ] as Array<keyof Customer>);
    return Array.from(fields).some((field) => this.isFieldDirty(field));
  }

  private normalizeFieldValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }

  canSave(): boolean {
    if (!this.isDirty) {
      return false;
    }
    if (!this.isNew) {
      return true;
    }
    return this.requiredCreateFields.every((field) => !this.isRequiredMissing(field));
  }

  canUndo(): boolean {
    return this.isDirty || this.isNew;
  }

  canAddNew(): boolean {
    return !this.isNew && !this.isDirty;
  }

  canOpenMoreMenu(): boolean {
    return !this.isNew && !this.isDirty && !!this.selectedCustomer;
  }
}
